import { useState, useEffect } from "react";
import { LoadingButton } from "@mui/lab";
import { TextField, InputLabel, IconButton } from "@mui/material";
import { ApiRequest } from "GlobalFunctions";
import { useSnackbar } from 'notistack';
import { useLocation } from "react-router-dom";
import { PiEyeSlashLight, PiEyeLight } from "react-icons/pi";

import { PiCheckLight } from "react-icons/pi";
import { PiXLight } from "react-icons/pi";

const defaultValues = {
    name: "",
    company: "",
    email: "",
    password: ""
};

export default function SignupForm(props: any) {
    const { setLoading } = props;
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();

    const [formValues, setFormValues] = useState(defaultValues);
    const [showPassword, setShowPassword] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);

    // Get transfer ID from URL parameters
    const getTransferId = () => {
        const searchParams = new URLSearchParams(location.search);
        return searchParams.get('tid');
    };

    // Pre-fill email if coming from accept-transfer
    useEffect(() => {
        if (location.pathname.includes('accept-transfer')) {
            const searchParams = new URLSearchParams(location.search);
            const email = searchParams.get('email');
            
            if (email) {
                setFormValues(prev => ({
                    ...prev,
                    email: decodeURIComponent(email)
                }));
            }
        }
    }, [location]);

    const [passwordRequirements, setPasswordRequirements] = useState([
        { text: 'A minimum of 10 characters', valid: false },
        { text: 'at least one number', valid: false },
        { text: 'at least one lowercase letter', valid: false },
        { text: 'at least one uppercase letter', valid: false }
    ]);

    useEffect(() => {
        const { password } = formValues;

        setPasswordRequirements([
            { text: 'A minimum of 10 characters', valid: password.length >= 10 },
            { text: 'at least one number', valid: /\d/.test(password) },
            { text: 'at least one lowercase letter', valid: /[a-z]/.test(password) },
            { text: 'at least one uppercase letter', valid: /[A-Z]/.test(password) }
        ]);
    }, [formValues.password]);

    const validateEmail = async () => {
        const { email } = formValues;
        let valid = true;
        await ApiRequest({
            method: 'get',
            url: '/users/validateEmail',
            query: { email },
            setResponse: (response: any) => {
                if(response.status !== "success"){
                    enqueueSnackbar(response.message, { variant: 'error' });
                    valid = false;
                }
            }
        });

        return valid;
    }

    const validatePassword = async () => {
        let valid = true;
        passwordRequirements.forEach(requirement => {
            if(!requirement.valid) valid = false;
        });

        if(!valid){
            enqueueSnackbar('Password Is Invalid', { variant: 'error' });
        }

        return valid;
    }

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const submit = async () => {
        setLoadingButton(true);
        
        // Validate required fields
        if (!formValues.name || !formValues.company || !formValues.email || !formValues.password) {
            enqueueSnackbar('All fields are required', { variant: 'error' });
            setLoadingButton(false);
            return;
        }

        const emailValid = await validateEmail();
        const passwordValid = await validatePassword();

        if(emailValid && passwordValid){
            const transferId = getTransferId();
            
            // Prepare the request body
            const requestBody = {
                ...formValues,
                ...(transferId && { transfer_id: transferId }) // Include transfer_id if it exists
            };
            
            // Here you would typically make an API call to create the user
            ApiRequest({
                url: '/stores/storeSignup',
                method: 'POST',
                body: requestBody,
                setResponse: (response: any) => {
                    if(response.status === "success"){
                        localStorage.setItem('ltkn', response.ltkn);
                        localStorage.setItem('role_id', response.role_id);
                        window.location.reload();
                        
                    } else {
                        enqueueSnackbar(response.message, { variant: 'error' });
                    }
                    setLoadingButton(false);
                }
            });
        } else {
            setLoadingButton(false);
        }
    }

    return (
        <div className="signup_form_container">
            
            <form className="signup_form" onSubmit={(e) => {
                e.preventDefault();
                submit();
            }}>
                <div className="textfield_wrapper">
                    <InputLabel className="login_label">Name</InputLabel>
                    <TextField
                        variant="outlined"
                        value={formValues.name}
                        onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                        fullWidth
                    />
                </div>

                <div className="textfield_wrapper">
                    <InputLabel className="login_label">Company</InputLabel>
                    <TextField
                        variant="outlined"
                        value={formValues.company}
                        onChange={(e) => setFormValues({ ...formValues, company: e.target.value })}
                        fullWidth
                    />
                </div>

                <div className="textfield_wrapper">
                    <InputLabel className="login_label">Email</InputLabel>
                    <TextField
                        variant="outlined"
                        type="email"
                        value={formValues.email}
                        onChange={(e) => setFormValues({ ...formValues, email: e.target.value })}
                        fullWidth
                    />
                </div>

                <div className="textfield_wrapper">
                    <InputLabel className="login_label">Password</InputLabel>
                    <TextField
                        variant="outlined"
                        value={formValues.password}
                        onChange={(e) => setFormValues({ ...formValues, password: e.target.value })}
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        InputProps={{
                            endAdornment: (
                                <IconButton 
                                    onClick={handleTogglePasswordVisibility}
                                    edge="end"
                                    className="show_password_button"
                                >
                                    {showPassword ? 
                                        <PiEyeLight size={24} color="#B9B9B9" /> : 
                                        <PiEyeSlashLight size={24} color="#B9B9B9"/>
                                    }
                                </IconButton>
                            )
                        }}
                    />
                </div>

                <ul className="password_requirements" style={{ listStyle: 'none', padding: 0 }}>
                    {passwordRequirements.map((requirement, index) => (
                        <li 
                            key={index} 
                            className="password_requirement" 
                            style={{ 
                                color: requirement.valid ? '#24BD55' : '#CCCCCC',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {requirement.valid ? 
                                <PiCheckLight size={16} color="#24BD55" style={{marginRight: 8}} /> : 
                                <PiXLight size={16} color="#CCCCCC" style={{marginRight: 8}} />
                            }
                            {requirement.text}
                        </li>
                    ))}
                </ul>

                <LoadingButton
                    loading={loadingButton}
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                >
                    Sign Up
                </LoadingButton>
            </form>
        </div>
    );
}